import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { ToastrService } from 'ngx-toastr';
import { Subject, take, takeUntil } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { PosSettingsService } from 'src/app/services/pos-settings.service';
import { PosOpeningCashDrawerService } from 'src/app/services/pos_OpeningCashDrawer.service';
import { OperationRentalService } from 'src/app/services/Rentals.service';
import { ReportsService } from 'src/app/services/report.service';
import { UserService } from 'src/app/services/User.service';
import { VendorsPayoutService } from 'src/app/services/VendorsPayout.service';
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { IPOSTerminal } from 'src/app/Shared/Interfaces/IPOSSettings';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { IReportResponse } from 'src/app/Shared/model/ReportsModel';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-vendors-payout',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, WordReplacePipe, LoaderComponent, UserPermissionHandlerDirective, TooltipModule],
  providers: [FilterService],
  standalone: true,
  templateUrl: './ops-vendors-payout.html',
  styleUrl: './ops-vendors-payout.scss'
})

export class OpsVendorsPayout implements OnInit, OnDestroy {

  @ViewChild('grid', { static: false }) public grid: GridComponent;

  private destroy$ = new Subject<void>();
  loaderIcon: Promise<boolean>;
  isCollapsed = true;
  userName = '';
  lastProcessedDate: Date | null = null;
  lastPayoutDate: Date | null = null;
  vendorsPayoutForm!: FormGroup;
  vendorsPayoutList: any[] = [];
  isCalculateEnabled: boolean = true;
  isOtherButtonsEnabled: boolean = false;
  isSaveEnabled: boolean = false;
  posTerminals: IPOSTerminal[] = [];
  minExpiryDate: Date = new Date();
  maxExpiryDate: Date = new Date();
  reportUrl;
  marketId: any;
  public datePipe = new DatePipe('en-US');
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.VendorsPayout, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.VendorsPayout, Action: EActionTypes.CREATE };
  selectedAutoDeductVendors: string[] = [];
  autoDeductVendorString: string = '';
  pendingDayEndList: any[] = [];
  splitCommissionByPaymentType: boolean = false;
  filteredVendorsPayoutList: any[] = [];
  SearchFields: any[] = [];
  DropDownSearchFields = { text: 'text', value: 'id' };
  advSearchLovId: string | null = null;
  advSearchKeyword: string = '';
  vendorTerm: string = 'Vendor';
  boothTerm: string = 'Booth';
  includeOnlineSales: boolean = false;
  sortBy: number = 1; // 1 = Vendor Name, 0 = Vendor ID
  displayVendorIdFilterInPayout: boolean = false;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private service: ReportsService,
    private generalService: GeneralService,
    private operationRentalService: OperationRentalService,
    private vendorsPayoutService: VendorsPayoutService,
    private posTerminalService: PosOpeningCashDrawerService,
    private posSettingsService: PosSettingsService,
    private wordReplacePipe: WordReplacePipe,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
  ) { }

  ngOnInit(): void {
    this.userName = this.userService.getLoggedInUserName();
    this.vendorTerm = this.wordReplacePipe.transform('Vendor');
    this.boothTerm = this.wordReplacePipe.transform('Booth');
    this.initSearchFields();
    this.operationRentalService.getDefaultMarketId().subscribe((res: any) => {
      if (res) {
        this.marketId = res;
      }
    });
    this.loadCustomMarketSettings();
    this.initForm();
    this.getReportUrl();
    this.getPosSettings();
    this.setInitialDates();
    this.loadPOSTerminals();
  }

  initSearchFields(): void {
    this.SearchFields = [
      { id: 'VendorAlphaID', text: `${this.vendorTerm} ID` },
      { id: 'VendorName', text: `${this.vendorTerm} Name` }
    ];
  }

  initForm(): void {
    this.vendorsPayoutForm = this.fb.group({
      fromDate: [''],
      toDate: [''],
      Terminal: [null],
    });
  }

  getReportUrl() {
    this.generalService.getReportUrl().subscribe((response: any) => {
      this.reportUrl = response;
    });
  }

  getPosSettings() {
    this.posSettingsService.getPosSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) {
          this.includeOnlineSales = res?.SalesTypesSettings?.AllowShipped ?? false;
        }
      });
  }

  getDefaultTerminalId(): void {
    this.userService.getDefaultTerminalId().pipe(take(1)).subscribe({
      next: (terminalId: number) => {
        const terminalObj = this.posTerminals.find(x => x.Id == terminalId);
        this.vendorsPayoutForm.patchValue({
          Terminal: terminalId > 0 ? terminalObj.Name : null
        });
      },
      error: (err) => {
        console.error('Failed to load default terminal.', err);

        this.vendorsPayoutForm.patchValue({
          Terminal: null
        });
      }
    });
  }

  invokeReport(obj) {
    var openInNewTab = '_blank';
    this.loaderIcon = Promise.resolve(true);
    this.service.generateReport(obj).subscribe((response: IReportResponse) => {
      if (response.Success) {
        window.open(this.reportUrl + `report?id=` + response.ReportRequestId,
          openInNewTab, 'width=' + (screen.availWidth - 5) + ',height=' + (screen.availHeight - 5));
        this.loaderIcon = Promise.resolve(false);
      } else {
        console.log('Error on generating Operational Report');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onReportView(reportType: string) {
    const obj = {
      ReportName: reportType,
      ReportParameters: {},
      MarketId: this.marketId
    };

    obj.ReportParameters["MarketId"] = this.marketId;
    obj.ReportParameters["FromDate"] = this.datePipe.transform(
      this.vendorsPayoutForm.controls['fromDate'].value,
      'MM/dd/yyyy'
    );
    obj.ReportParameters["ToDate"] = this.datePipe.transform(
      this.vendorsPayoutForm.controls['toDate'].value,
      'MM/dd/yyyy'
    );
    obj.ReportParameters["IsPreview"] = Number(1);
    obj.ReportParameters["OrderBy"] = Number(this.sortBy);
    obj.ReportParameters["IncludeOnlineSales"] = this.includeOnlineSales ? 1 : 0;
    obj.ReportParameters["IsCommissionSplitByPaymentType"] = this.splitCommissionByPaymentType ? 1 : 0;

    if (this.selectedAutoDeductVendors.length > 0) {
      obj.ReportParameters["@AutoDeductVendorIds"] = this.autoDeductVendorString
    }
    obj.ReportParameters["VTerm"] = this.vendorTerm;
    obj.ReportParameters["BTerm"] = this.boothTerm;

    this.loaderIcon = Promise.resolve(false);
    this.invokeReport(obj);
  }

  loadCustomMarketSettings(): void {
    this.generalService.getCustomMarketSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.splitCommissionByPaymentType = res.SplitCommissionByPaymentType ?? false;
          this.displayVendorIdFilterInPayout = res.AntiqueVillageChanges ?? false;
          this.sortBy = this.displayVendorIdFilterInPayout ? 0 : 1;
        },
        error: (err) => {
          console.error('Failed to load custom market settings:', err);
        }
      });
  }

  setInitialDates(): void {
    this.loaderIcon = Promise.resolve(true);
    this.vendorsPayoutService.getLastProcessedVendorPayoutDate()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.lastProcessedDate = res?.LastProcessedDate ? new Date(res.LastProcessedDate) : null;
          this.lastPayoutDate = res?.LastPayoutDate ? new Date(res.LastPayoutDate) : null;
          let fromDateObj: Date;
          if (!this.lastProcessedDate) {
            fromDateObj = new Date();
          } else {
            fromDateObj = new Date(this.lastProcessedDate);
            fromDateObj.setDate(fromDateObj.getDate() + 1);
          }

          this.minExpiryDate = fromDateObj;
          const toDateObj = new Date();
          this.vendorsPayoutForm.patchValue({
            fromDate: fromDateObj,
            toDate: toDateObj
          });
          this.isFromDateEditable();
          this.loaderIcon = Promise.resolve(false);
        },
        error: (err) => {
          this.toastr.error('Failed to load last processed date.');
          console.error(err);
          this.vendorsPayoutForm.patchValue({
            fromDate: new Date(),
            toDate: new Date()
          });
          this.loaderIcon = Promise.resolve(false);
        }
      });
  }

  loadPOSTerminals(): void {
    this.posTerminalService.getPOSTerminals()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.posTerminals = res;
          this.getDefaultTerminalId();
        },
        error: (err) => {
          console.error('Failed to load POS terminals:', err);
        }
      });
  }

  loadPendingDayEndList(startDate: string, endDate: string): void {
    this.vendorsPayoutService.getPendingDayEndList(startDate, endDate).pipe(takeUntil(this.destroy$)).subscribe({
      next: res => {
        this.pendingDayEndList = res || [];
      },
      error: err => {
        console.error('Failed to load pending day end list', err);
        this.pendingDayEndList = [];
      }
    });
  }

  async calculateVendorsPayout() {
    const fromDate = this.vendorsPayoutForm.get('fromDate')?.value;
    const toDate = this.vendorsPayoutForm.get('toDate')?.value;

    if (!fromDate || !toDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Dates',
        text: 'Please select both From Date and To Date before calculating payout.',
      });
      return;
    }
    const formattedToDate = this.formatDateMMDDYYYY(toDate);
    const messages = [
      'Have you run the Automatic Rent Renewal (ARR) process for the next month?',
      'Have you checked the "Auto Deduct Rent" option for whom rent needs to be deducted from the Payout process?',
      `Have you done the ${this.vendorTerm} adjustments if any?`,
      `Is the "ToDate" for the Payout process correct? Selected date: ${formattedToDate}?`
    ];

    for (let i = 0; i < messages.length; i++) {
      const result = await Swal.fire({
        title: `Payout Reminders`,
        text: messages[i],
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
      });
      if (!result.isConfirmed) {
        this.isCalculateEnabled = true;
        this.isOtherButtonsEnabled = false;
        return;
      }
    }

    Swal.fire({
      title: 'Processing...',
      text: `${this.vendorTerm}s Payout calculation in progress!. Please wait.`,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    const startDate = this.formatDate(fromDate);
    const endDate = this.formatDate(toDate);

    this.vendorsPayoutService.getVendorsPayout(startDate, endDate).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any[]) => {
        this.vendorsPayoutList = res || [];
        this.filteredVendorsPayoutList = [...this.vendorsPayoutList];
        this.sortVendors();
        this.isCalculateEnabled = false;
        this.isOtherButtonsEnabled = true;
        this.isSaveEnabled = true;
        Swal.close();
        // console.log('Vendors Payout:', this.vendorsPayoutList);
      },
      error: (err) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `An error occurred while fetching ${this.vendorTerm} payouts.`,
        });
        console.error(`Error fetching ${this.vendorTerm} payout:`, err);
      },
    });

    this.loadPendingDayEndList(startDate, endDate);
  }

  saveVendorsPayout() {
    this.clearAdvSearch();
    const fromDate = this.vendorsPayoutForm.get('fromDate')?.value;
    const toDate = this.vendorsPayoutForm.get('toDate')?.value;

    const lastPayout = new Date(this.lastPayoutDate);
    const today = new Date();
    const isSameDay = lastPayout.getFullYear() === today.getFullYear() &&
      lastPayout.getMonth() === today.getMonth() &&
      lastPayout.getDate() === today.getDate();

    if (isSameDay) {
      Swal.fire({
        title: `${this.vendorTerm}s Payout`,
        text: `Cannot perform two ${this.vendorTerm}s payout on the same day!`,
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!fromDate || !toDate) {
      this.toastr.warning(
        $localize`Please select both From Date and To Date before saving payout.`, $localize`Warning`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    if (!this.vendorsPayoutForm.get('Terminal')?.value) {
      Swal.fire({
        title: $localize`Missing Terminal`,
        text: $localize`Please select a terminal before saving ${this.vendorTerm} payouts.`,
        icon: 'warning',
        confirmButtonText: $localize`OK`,
      });
      return;
    }

    if (this.pendingDayEndList && this.pendingDayEndList.length > 0) {
      const pendingListMessage = this.pendingDayEndList.map((x: any) => `${x.Terminal}/${x.UserName} on ${x.OpenCashDate}`)
        .join('<br>');

      Swal.fire({
        title: $localize`Day End Pending`,
        html: `<div style="text-align: justify;">
                Please perform Day End for all terminals before running ${this.vendorTerm}s Payout process.<br><br>
                <b>Day End process pending for:</b><br>
                ${pendingListMessage}
              </div>`,
        icon: 'error',
        confirmButtonText: $localize`OK`
      });
      return;
    }

    const formattedFromDate = this.formatDateMMDDYYYY(fromDate);
    const formattedToDate = this.formatDateMMDDYYYY(toDate);

    Swal.fire({
      title: $localize`Are you sure?`,
      html: $localize`Do you want to save ${this.vendorTerm} payouts from <strong>${formattedFromDate}</strong> to <strong>${formattedToDate}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, save`,
      cancelButtonText: $localize`Cancel`,
    }).then((result) => {
      if (result.isConfirmed) {
        const payoutInfo = {
          UserName: this.userName || '',
          TerminalName: this.vendorsPayoutForm.get('Terminal')?.value || '',
          FromDate: this.formatDate(fromDate),
          ToDate: this.formatDate(toDate)
        };
        this.loaderIcon = Promise.resolve(true);
        this.vendorsPayoutService.saveVendorsPayout(this.vendorsPayoutList, payoutInfo)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res.Success) {
                this.loaderIcon = Promise.resolve(false);
                this.toastr.success(
                  `${this.vendorTerm}s payout saved successfully.`, $localize`Success`,
                  this.sharedHTMLSettings.ToasterConfig
                );
                this.isSaveEnabled = false;
              } else {
                this.loaderIcon = Promise.resolve(false);
                Swal.fire({
                  title: $localize`Payout Failed`,
                  text: res.Message || `Error saving ${this.vendorTerm}s payout.`,
                  icon: 'error',
                  confirmButtonText: $localize`OK`
                });
              }
            },
            error: (err) => {
              this.loaderIcon = Promise.resolve(false);
              console.error(`Failed to save ${this.vendorTerm} payout`, err);
              this.toastr.error(
                $localize`Failed to save ${this.vendorTerm} payout.`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              );
            }
          });
      }
    });
  }

  formatDate(date: string | Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateMMDDYYYY(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  isFromDateEditable(): boolean {
    if (!this.lastProcessedDate) {
      return true;
    }
    else
      return false;
  }

  onAutoDeductChange(event: any, row: any): void {
    const vendorId = row.VendorId;
    const isChecked = event.target.checked;
    //Find and update in MAIN list
    const indexMain = this.vendorsPayoutList.findIndex(x => x.VendorId === vendorId);
    if (indexMain === -1) return;
    //Update value
    this.vendorsPayoutList[indexMain].AutoDeductRent = isChecked;
    // Recalculate based on MAIN list row
    this.recalculateVendorAmounts(this.vendorsPayoutList[indexMain]);
    //Sync MAIN → FILTERED list which used in grid
    const indexFiltered = this.filteredVendorsPayoutList.findIndex(x => x.VendorId === vendorId);
    if (indexFiltered !== -1) {
      this.filteredVendorsPayoutList[indexFiltered] = { ...this.vendorsPayoutList[indexMain] };
      this.filteredVendorsPayoutList = [...this.filteredVendorsPayoutList];
    }

    this.updateAutoDeductOverride(vendorId, isChecked);

    const vendorName = row.VendorName;
    const vendorAlphaId = row.VendorAlphaID;
    const message = isChecked
      ? `Do you want to deduct rent from sales of '${vendorName}' (${this.vendorTerm} ID: ${vendorAlphaId}) for future payouts?`
      : `Do you want to cancel the rent deduction from sales of '${vendorName}' (${this.vendorTerm} ID: ${vendorAlphaId}) for future payouts?`;

    Swal.fire({
      title: 'Confirmation Required',
      text: message,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No'
    }).then(result => {
      if (result.isConfirmed) {
        const payload = {
          VendorId: row.VendorId,
          VendorAlphaId: row.VendorAlphaID,
          VendorName: row.VendorName,
          AutoDeductRent: isChecked
        };

        this.vendorsPayoutService.saveVendorAutoDeduct(payload).pipe(takeUntil(this.destroy$)).subscribe({
          next: res => {
            if (res?.Success) {
              this.toastr.success(res.Message, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
            } else {
              this.toastr.error(res.Message, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
            }
          },
          error: (err) => {
            this.toastr.error(err?.message || $localize`An unexpected error occurred`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
          }
        });
      }
    });

    setTimeout(() => this.grid?.refresh(), 0);
  }

  private recalculateVendorAmounts(row: any): void {
    const rentalDue = Number(row.RentalDue ?? 0);
    const netSales = Number(row.NetSales ?? 0);
    const autoDeduct = row.AutoDeductRent;

    let payToVendor = 0;
    let vendorOwes = 0;

    if (netSales >= 0) {
      payToVendor = netSales;
    }

    if (autoDeduct && netSales > 0) {
      if (rentalDue < 0) {
        const dueAmount = Math.abs(rentalDue);
        if (netSales >= dueAmount) {
          payToVendor -= dueAmount;
          vendorOwes = 0;
        } else {
          payToVendor = 0;
          vendorOwes = (dueAmount - netSales) * -1;
        }
      }
      else {
        vendorOwes = 0;
      }
    } else if (autoDeduct && netSales == 0 && rentalDue > 0) {
      vendorOwes = 0;
      payToVendor = 0; //rentalDue
    }
    else if (autoDeduct && netSales <= 0) {
      vendorOwes = rentalDue + netSales;
      payToVendor = 0;
    }
    else {
      vendorOwes = rentalDue;
      payToVendor = netSales;
    }

    const index = this.vendorsPayoutList.findIndex(x => x.VendorId === row.VendorId);

    if (index !== -1) {
      this.vendorsPayoutList[index] = {
        ...this.vendorsPayoutList[index],
        PayToVendor: Number(payToVendor.toFixed(2)),
        VendorOwes: Number(vendorOwes.toFixed(2)),
        AutoDeductRent: row.AutoDeductRent
      };
      //console.log(this.vendorsPayoutList[index])
      this.vendorsPayoutList = [...this.vendorsPayoutList];
    }
  }

  private updateAutoDeductOverride(vendorId: number, value: boolean): void {
    // Remove existing override for this vendor
    this.selectedAutoDeductVendors = this.selectedAutoDeductVendors.filter(v => !v.startsWith(`${vendorId}:`));
    // Add updated override
    this.selectedAutoDeductVendors.push(`${vendorId}:${value ? 1 : 0}`);
    // Final string → "101:1,102:0"
    this.autoDeductVendorString = this.selectedAutoDeductVendors.join(',');
    //console.log(this.autoDeductVendorString);
  }

  formatAccountingCurrency(value: number | null): string {
    const num = value ?? 0;
    if (num === 0) return `$0.00`;
    if (num < 0) {
      return `($${Math.abs(num).toFixed(2)})`;
    }
    return `$${num.toFixed(2)}`;
  }

  parseDateFromApi(dateStr: string): Date {
    const parts = dateStr.split('-'); // ["2025", "08", "17"]
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  onStartDateChange(event: any): void {
    const startDate = event.value;
    if (startDate) {
      this.minExpiryDate = new Date(startDate);
    }
  }

  cancelPayout() {
    if (!this.isSaveEnabled) {
      this.resetButtons();
      this.vendorsPayoutForm.reset();
      this.setInitialDates();
      this.vendorsPayoutList = [];
      this.getDefaultTerminalId();
      this.filteredVendorsPayoutList = [];
      if (this.grid) {
        this.grid.refresh();
      }
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to cancel the payout process? All changes will be reset.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'No, keep',
    }).then((result) => {
      if (result.isConfirmed) {
        this.resetButtons();
        this.vendorsPayoutForm.reset();
        this.setInitialDates();
        this.getDefaultTerminalId();
        this.vendorsPayoutList = [];
        this.filteredVendorsPayoutList = [];
        if (this.grid) {
          this.grid.refresh();
        }
      }
    });
  }

  resetButtons() {
    this.isCalculateEnabled = true;
    this.isOtherButtonsEnabled = false;
    this.isSaveEnabled = false;
  }

  searchWithAdv(): void {
    if (!this.advSearchLovId || !this.advSearchKeyword?.trim()) {
      // Nothing selected → reset to full list
      this.filteredVendorsPayoutList = [...this.vendorsPayoutList];
      return;
    }

    const keyword = this.advSearchKeyword.trim().toLowerCase();
    const field = this.advSearchLovId;

    const result = this.vendorsPayoutList.filter(item => {
      const value = item[field];
      if (value === null || value === undefined) {
        return false;
      }
      return value.toString().toLowerCase().includes(keyword);
    });

    if (result.length === 0) {
      this.toastr.info(
        `No matching ${this.getSearchFieldLabel(field)} found for "${this.advSearchKeyword}".`,
        'No Records Found',
        this.sharedHTMLSettings.ToasterConfig
      );
      this.filteredVendorsPayoutList = [...this.vendorsPayoutList]
      return;
    }

    this.filteredVendorsPayoutList = result;
  }

  private getSearchFieldLabel(field: string): string {
    const map: Record<string, string> = {
      VendorAlphaID: `${this.vendorTerm} ID`,
      VendorName: `${this.vendorTerm} Name`
    };

    return map[field] ?? 'records';
  }

  clearAdvSearch(): void {
    this.advSearchLovId = null;
    this.advSearchKeyword = '';
    this.filteredVendorsPayoutList = [...this.vendorsPayoutList];
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  sortVendors(): void {
    const list = [...this.filteredVendorsPayoutList];

    if (this.sortBy === 1) {
      // Vendor Name
      list.sort((a, b) =>
        (a.VendorName ?? '').toString().localeCompare(
          (b.VendorName ?? '').toString(),
          undefined,
          { sensitivity: 'base' }
        )
      );
    } else {
      // Vendor ID
      list.sort((a, b) => {
        const idA = (a.VendorAlphaID ?? '').toString().trim();
        const idB = (b.VendorAlphaID ?? '').toString().trim();

        const isNumericA = /^\d+$/.test(idA);
        const isNumericB = /^\d+$/.test(idB);

        // Numeric IDs first
        if (isNumericA && !isNumericB) return -1;
        if (!isNumericA && isNumericB) return 1;

        // Both numeric
        if (isNumericA && isNumericB) {
          const numA = Number(idA);
          const numB = Number(idB);

          // Sort by actual numeric value
          if (numA !== numB) {
            return numA - numB;
          }

          // Same numeric value → shorter ID first
          return idA.length - idB.length;
        }

        // Both alphanumeric
        return idA.localeCompare(idB, undefined, {
          numeric: true,
          sensitivity: 'base'
        });
      });
    }

    this.filteredVendorsPayoutList = list;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
